// Scoring v4, Part 4: three statements, each from a different gift. The taker picks the one most
// like them, then the one least like them from the other two. Gift names are never shown (the
// option's `value` is the gift, but only its text is). Styled like the rater test's most/least cards.
import { TRIAD_COPY } from "../lib/copy";

/** The picks so far. Only a draft with both is an answer. */
export type TriadDraft = { most?: string; least?: string };

export const isTriadComplete = (d: TriadDraft): d is { most: string; least: string } => !!d.most && !!d.least;

/**
 * Tap rules. Nothing picked: the tapped card is "most like you". After that, the "most" card can't be
 * "least": tapping it again clears both picks (start over), and tapping one of the other two sets or
 * moves the "least" pick.
 */
export function tapTriad(draft: TriadDraft, tapped: string): TriadDraft {
  if (!draft.most) return { most: tapped };
  if (tapped === draft.most) return {};
  if (tapped === draft.least) return draft;
  return { most: draft.most, least: tapped };
}

/** The words around the picks: the self-test's ("like you"), or a rater's ("like Sam"). */
export interface TriadCopy {
  statusMost: string;
  statusLeast: string;
  tagMost: string;
  tagLeast: string;
}

export function TriadPicker({
  options,
  value,
  onChange,
  copy = TRIAD_COPY,
}: {
  options: { value: string; text: string }[];
  value: TriadDraft;
  onChange: (next: TriadDraft) => void;
  copy?: TriadCopy;
}) {
  const tap = (v: string) => {
    const next = tapTriad(value, v);
    if (next !== value) onChange(next);
  };

  return (
    <div>
      <p className="mb-3 min-h-6 text-[14px] text-muted" aria-live="polite">
        {value.most ? copy.statusLeast : copy.statusMost}
      </p>
      <div role="group" aria-label="Your answer" className="flex flex-col gap-3">
        {options.map((o) => {
          const tag = value.most === o.value ? "most" : value.least === o.value ? "least" : null;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={tag !== null}
              aria-label={`${o.text}${tag === "most" ? `, ${copy.tagMost.toLowerCase()}` : tag === "least" ? `, ${copy.tagLeast.toLowerCase()}` : ""}`}
              onClick={() => tap(o.value)}
              className={`relative flex min-h-[96px] items-end rounded-[16px] border p-4 pt-10 text-left text-[17px] leading-snug font-medium ${
                tag === "most"
                  ? "border-accent bg-accent text-on-accent"
                  : tag === "least"
                    ? "border-ink bg-soft text-ink"
                    : "border-line bg-panel text-ink hover:bg-soft"
              }`}
            >
              {tag ? (
                <span
                  aria-hidden="true"
                  className={`eyebrow absolute top-3 left-3 rounded-[8px] px-1.5 py-0.5 ${
                    tag === "most" ? "bg-ink text-ground" : "border border-ink text-ink"
                  }`}
                >
                  {tag === "most" ? copy.tagMost : copy.tagLeast}
                </span>
              ) : null}
              {o.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}
